// Telefonassistent (Vocaris) als Kanal der öffentlichen API v1 (docs/integration/telefonassistent.md):
//
//   sales.channel.capabilities   jeder gültige Schlüssel (auch der Bestell-App): Was darf DIESER Schlüssel, welche Standorte und welcher
//                                Sammelkunde gehören dazu – berechnet aus den echten Rechten, nie aus einer festen Liste (TA-R36)
//   sales.phone.*                nur der technische Zugang mit der Rolle „Telefonassistent“ (`api_phone`): dieselbe Fachlogik wie die
//                                Kanal-Verträge der Bestell-App, aber immer in der sicheren Fassung – Preise nur zum gebundenen Sammelkunden
//                                (TA-R6), Lieferregeln ohne Gutscheincodes und „Lieferung frei“ (TA-R7), Gutschein ohne Kunde (TA-R8)
//
// Eigene Verträge statt der `sales.channel.*` der App (Abweichung TA-D1): Ein Vertrag prüft genau ein Recht, und die Rechte der
// Kanal-Verträge öffnen zugleich Kundenkonten, Aufträge, Abos und Einwilligungen. Die Bestell-App bleibt unverändert.
import { PERMISSION_AUTHENTICATED, defineContract } from '@shared/contract'
import { z } from 'zod'
import { channelAvailability, channelCatalog } from './channel'
import { channelCheckCoupon, channelCheckoutRules, channelSlotLoad } from './checkout'
import { zId } from '@shared/ids'
import { CASE_STATUSES, LEAD_OUTCOMES } from '../lib/cases'
import {
  ORDER_STAGES,
  PHONE_ORDER_HINTS,
  PHONE_ORDER_ID,
  PHONE_ORDER_MAX_LINE_QTY,
  PHONE_ORDER_MAX_LINES,
  PHONE_ORDER_PROBLEMS,
  PHONE_PAYMENT_METHODS,
} from '../lib/phone'
import { zDay, zTime } from './common'

const READ = 'sales.phone.read'
const LEAD = 'sales.phone.lead'
const zText = (max: number) => z.string().trim().max(max)

// ---- Fähigkeiten (alle Schlüssel) ------------------------------------------------------------------------------------------------------

export const channelCapabilities = defineContract({
  name: 'sales.channel.capabilities',
  title: 'Fähigkeiten eines Zugangs der öffentlichen Schnittstelle abrufen',
  description:
    'Welche Aktionen dieser Schlüssel aufrufen darf, welche Standorte und welcher Sammelkunde zu ihm gehören und was diese Version kann – ohne Probeaufrufe mit Wirkung.',
  llm: {
    description:
      'Öffentliche Schnittstelle (jeder gültige API-Schlüssel). Liefert `actions` – genau die öffentlichen Aktionen, die dieser Schlüssel mit seinen ' +
      'Rechten aufrufen darf –, den Zugang (`access`), die Standorte mit Lieferung bzw. Abholung, den gebundenen Sammelkunden (nur Telefonassistent), ' +
      'Kontingente und Fähigkeiten (`features`). Ändert nichts.',
    keywords: ['Fähigkeiten', 'Rechte', 'Schnittstelle', 'Telefonassistent', 'Bestell-App'],
  },
  input: z.strictObject({}),
  output: z.object({
    api: z.literal('public-v1'),
    serverTime: z.date(),
    product: z.object({ name: z.string(), version: z.string().nullable() }),
    access: z.object({ channel: z.string(), role: z.string(), label: z.string() }),
    actions: z.array(z.string()),
    features: z.object({
      caseKinds: z.array(z.string()),
      orderLookup: z.boolean(),
      customerHint: z.boolean(),
      orderDrafts: z.boolean(),
      checkoutRulesRedacted: z.boolean(),
      /** Telefon-Bestellungen (unbestätigt): alle drei Aktionen `sales.phone.checkCustomer | quoteOrder | submitOrder` aufrufbar (§16) */
      orders: z.boolean(),
    }),
    locations: z.array(
      z.object({
        code: z.string(),
        name: z.string(),
        delivery: z.boolean(),
        pickup: z.boolean(),
        /** Abholfenster eingerichtet: Abholung nur in einem Fenster; sonst gilt die Öffnungszeit des Tages (§16) */
        pickupWindows: z.boolean(),
      }),
    ),
    collectiveCustomerNumber: z.string().nullable(),
    limits: z.object({
      perMinute: z.int(),
      orderLookupPerMinute: z.int().nullable(),
      /** Grenzen der Telefon-Bestellungen – null ohne `features.orders` */
      order: z
        .object({
          maxGrossCents: z.int(),
          maxUnits: z.int(),
          maxLines: z.int(),
          maxLineQty: z.int(),
          perPhoneHour: z.int(),
          perPhoneDay: z.int(),
          openPerPhone: z.int(),
          /** je Zugang (§16.12, Tagesgrenze am Zugang) */
          perAccessHour: z.int(),
          perAccessDay: z.int(),
        })
        .nullable(),
    }),
    aliases: z.record(z.string(), z.string()),
  }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
  publicApi: true,
  assistant: 'never',
})

// ---- Lesen: Sortiment, Preise, Lieferregeln, Belegung, Gutschein ------------------------------------------------------------------------

export const phoneCatalog = defineContract({
  name: 'sales.phone.catalog',
  title: 'Sortiment für den Telefonassistenten lesen',
  description: 'Artikel mit Verkaufseinheit und Warengruppe – seitenweise, mit `updatedSince` nur Geänderte.',
  llm: {
    description:
      'Schnittstelle des Telefonassistenten (öffentliche API). Dieselbe Ausgabe wie sales.channel.catalog: Artikel der Arten Ware und Mietartikel ' +
      '(auch gesperrte, dann `active: false`), Warengruppen und Rückgutarten auf der ersten Seite. Preise und Bestand: sales.phone.availability.',
    keywords: ['Sortiment', 'Katalog', 'Telefonassistent', 'Schnittstelle'],
  },
  input: channelCatalog.input,
  output: channelCatalog.output,
  permission: READ,
  risk: 'read',
  scope: 'any',
  publicApi: true,
  assistant: 'never',
})

export const phoneAvailability = defineContract({
  name: 'sales.phone.availability',
  title: 'Verfügbarkeit und Preise für den Telefonassistenten lesen',
  description: 'Freier Bestand und Preis je Artikelnummer und Gebinde – immer zu den Preisen des gebundenen Sammelkunden.',
  llm: {
    description:
      'Schnittstelle des Telefonassistenten (öffentliche API). Wie sales.channel.availability, aber höchstens 50 Artikelnummern und immer mit den ' +
      'Preisen des Sammelkunden, an den der Zugang gebunden ist. `customerNumber` darf fehlen oder muss genau dieser Sammelkunde sein – eine andere ' +
      'Nummer wird abgelehnt (403 `customer_not_allowed`), damit am Telefon keine Vertragspreise erscheinen.',
    keywords: ['Verfügbarkeit', 'Preise', 'Bestand', 'Telefonassistent', 'Schnittstelle'],
  },
  input: z.strictObject({
    ...channelAvailability.input.shape,
    skus: channelAvailability.input.shape.skus.max(50, { error: 'Höchstens 50 Artikelnummern auf einmal.' }),
  }),
  output: channelAvailability.output,
  permission: READ,
  risk: 'read',
  scope: 'any',
  publicApi: true,
  assistant: 'never',
})

export const PHONE_REDACTED = ['coupons', 'freeDeliveryCustomerNumbers'] as const

export const phoneCheckoutRules = defineContract({
  name: 'sales.phone.checkoutRules',
  title: 'Lieferregeln für den Telefonassistenten abrufen',
  description: 'Zeitfenster, Ausnahmetage, Bestellschluss, Liefergebiete, Tragservice und Marktdaten – ohne Gutscheincodes und „Lieferung frei“.',
  llm: {
    description:
      'Schnittstelle des Telefonassistenten (öffentliche API). Wie sales.channel.checkoutRules (je Standort des Zugangs Fenster, Ausnahmetage, ' +
      'Bestellschluss, Haltedauer, Liefergebiete mit Gebühren, Öffnungszeiten und Hinweis), aber `coupons` und `freeDeliveryCustomerNumbers` sind ' +
      'immer leer und in `redacted` genannt. `version` ist der Hash über genau diese Fassung.',
    keywords: ['Lieferregeln', 'Zeitfenster', 'Liefergebiete', 'Öffnungszeiten', 'Telefonassistent', 'Schnittstelle'],
  },
  input: z.strictObject({}),
  output: channelCheckoutRules.output.extend({ redacted: z.array(z.enum(PHONE_REDACTED)) }),
  permission: READ,
  risk: 'read',
  scope: 'any',
  publicApi: true,
  assistant: 'never',
})

export const phoneSlotLoad = defineContract({
  name: 'sales.phone.slotLoad',
  title: 'Belegung der Zeitfenster für den Telefonassistenten abrufen',
  description: 'Gebuchte Aufträge je Tag und Fenster eines Standorts (höchstens 14 Tage).',
  llm: {
    description:
      'Schnittstelle des Telefonassistenten (öffentliche API). Wie sales.channel.slotLoad: je Fenster des Standorts `locationCode` und der Art ' +
      '(`delivery` oder `pickup`) die Zahl der gebuchten Aufträge von … bis … (höchstens 14 Tage).',
    keywords: ['Belegung', 'Zeitfenster', 'ausgebucht', 'Telefonassistent', 'Schnittstelle'],
  },
  input: channelSlotLoad.input,
  output: channelSlotLoad.output,
  permission: READ,
  risk: 'read',
  scope: 'record',
  publicApi: true,
  assistant: 'never',
})

const couponShape = channelCheckCoupon.input.shape

export const phoneCheckCoupon = defineContract({
  name: 'sales.phone.checkCoupon',
  title: 'Gutschein für den Telefonassistenten prüfen',
  description: 'Prüft einen genannten Gutscheincode für einen Warenwert und eine Kundenart – ohne Kundennummer und E-Mail.',
  llm: {
    description:
      'Schnittstelle des Telefonassistenten (öffentliche API). Wie sales.channel.checkCoupon, aber ohne `customerNumber` und `email` (die Grenze je ' +
      'Kunde bleibt dem Bestellvorgang überlassen). Liefert `valid`, den Grund einer Ablehnung, einen Satz für die Kundschaft und den Rabatt. Bucht nichts.',
    keywords: ['Gutschein prüfen', 'Rabattcode', 'Telefonassistent', 'Schnittstelle'],
  },
  input: z.strictObject({
    code: couponShape.code,
    itemsGrossCents: couponShape.itemsGrossCents,
    customerType: couponShape.customerType,
    day: couponShape.day,
  }),
  output: channelCheckCoupon.output,
  permission: READ,
  risk: 'read',
  scope: 'any',
  publicApi: true,
  assistant: 'never',
})

// ---- Interessent (Anliegen-Art `lead`, docs/integration/telefonassistent.md §6) -------------------------------------------------------

export const phoneSubmitCase = defineContract({
  name: 'sales.phone.submitCase',
  title: 'Interessenten aus dem Telefonassistenten melden',
  description: 'Ein Anrufer möchte Kunde werden: Er erscheint als Anliegen „Interessent (Telefon)“ mit einer Aufgabe für den Vertrieb.',
  llm: {
    description:
      'Schnittstelle des Telefonassistenten (öffentliche API). Meldet einen Interessenten als Anliegen der Art `lead` (nur diese Art; andere → 403 ' +
      '`case_kind_not_allowed`): Name, Rückrufnummer (E.164) mit Herkunft, Art, Anliegen, Einwilligung in den Rückruf (`yes`), Zeitpunkt – alles Text. ' +
      'Idempotent über `caseId` (`voc-int-<32 Hex>`): gleiche Angaben → `replayed: true`, andere → 409 `case_id_reused`.',
    keywords: ['Interessent', 'Rückruf', 'Neukunde', 'Telefonassistent', 'Schnittstelle'],
  },
  input: z.strictObject({
    caseId: zText(64).min(1, { error: 'Die Kennung des Interessenten fehlt.' }),
    kind: zText(40).min(1, { error: 'Die Art des Anliegens fehlt.' }),
    payload: z.record(z.string(), z.string({ error: 'Alle Angaben zum Interessenten sind Text.' }), { error: 'Die Angaben zum Interessenten fehlen.' }),
  }),
  output: z.object({ caseId: z.string(), status: z.enum(CASE_STATUSES), replayed: z.boolean() }),
  permission: LEAD,
  risk: 'write',
  scope: 'record',
  publicApi: true,
  assistant: 'never',
  // Name, Telefon, Anschrift: nicht ins versiegelte Audit-Protokoll – sie stehen nur im Anliegen und werden dort geleert (TA-D8)
  auditInput: 'omit',
})

export const phoneCases = defineContract({
  name: 'sales.phone.cases',
  title: 'Stand der eigenen Interessenten für den Telefonassistenten abrufen',
  description: 'Nur die Interessenten dieses Zugangs, ohne Angaben – mit Zustand und Ergebnis, aufsteigend nach Cursor.',
  llm: {
    description:
      'Schnittstelle des Telefonassistenten (öffentliche API). Liefert höchstens 100 eigene Interessenten nach dem Cursor `after` (`<Epoche>:<seq>`), ' +
      'ohne Angaben (`payload` leer) und mit `decision.outcome` (`customer_created`, `contacted`, `discarded`, `expired`) – ohne Kundennummer.',
    keywords: ['Interessent', 'Stand', 'Telefonassistent', 'Schnittstelle'],
  },
  input: z.strictObject({ after: zText(80).optional() }),
  output: z.object({
    cases: z.array(
      z.object({
        caseId: z.string(),
        kind: z.literal('lead'),
        status: z.enum(CASE_STATUSES),
        seq: z.int(),
        payload: z.strictObject({}),
        decision: z.object({ outcome: z.enum(LEAD_OUTCOMES) }).nullable(),
      }),
    ),
    cursor: z.string(),
  }),
  permission: LEAD,
  risk: 'read',
  scope: 'any',
  publicApi: true,
  assistant: 'never',
})

// ---- Auftragsstand mit Auftragsnummer und Postleitzahl (docs/integration/telefonassistent.md §7) ------------------------------------------

const zHm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)

export const phoneLookupOrder = defineContract({
  name: 'sales.phone.lookupOrder',
  title: 'Auftragsstand für den Telefonassistenten abfragen',
  description: 'Stand, Tag und Zeitfenster eines Auftrags – nur mit Auftragsnummer und passender Postleitzahl, gesperrt nach Fehlversuchen.',
  llm: {
    description:
      'Schnittstelle des Telefonassistenten (öffentliche API). Sucht einen Auftrag der letzten 14 Tage über die genannte Nummer (Auftragsnummer oder ' +
      'Nummer der Bestell-App, normalisiert; ohne Buchstaben nur über die Ziffern) und prüft die Postleitzahl (Lieferung: Lieferanschrift, Abholung: ' +
      'Anschrift der Kundin bzw. des Kunden). Nicht gefunden, falsche Postleitzahl oder zu alt: immer `{ found: false }`. Gefunden: nur Art, Stufe, ' +
      'Tag, Zeitfenster, Ankunftsfenster, abholbereit bis. Mehrere Treffer → `{ found: false, ambiguous: true }` (zählt als Fehlversuch); 5 Fehlversuche ' +
      'je Nummer bzw. Auftrag oder 30 je Zugang in 60 Minuten ' +
      'oder mehr als 30 Abfragen je Minute → 429.',
    keywords: ['Auftragsstand', 'Bestellung', 'Wo bleibt', 'Lieferung', 'Abholung', 'Telefonassistent', 'Schnittstelle'],
  },
  input: z.strictObject({
    number: zText(40).min(1, { error: 'Bitte die Auftragsnummer nennen.' }),
    zip: z.string().trim().regex(/^[0-9]{5}$/, { error: 'Eine Postleitzahl hat fünf Ziffern.' }),
  }),
  output: z.union([
    /** ambiguous: mehrere Aufträge passen zu Nummer und Postleitzahl – kein Stand, die vollständige Nummer nennen lassen */
    z.strictObject({ found: z.literal(false), ambiguous: z.literal(true).optional() }),
    z.strictObject({
      found: z.literal(true),
      number: z.string(),
      kind: z.enum(['delivery', 'pickup']),
      stage: z.enum(ORDER_STAGES),
      stageLabel: z.string(),
      day: z.string().nullable(),
      window: z.object({ from: zHm, to: zHm }).nullable(),
      eta: z.object({ from: zHm, to: zHm }).nullable(),
      readyUntil: z.date().nullable(),
      updatedAt: z.date(),
    }),
  ]),
  permission: 'sales.phone.orderLookup',
  // zählt Versuche (Sperren nach Fehlversuchen) – darum ändernd; „nicht gefunden“ ist ein Ergebnis, kein Fehler, sonst rollte die
  // Zählung mit zurück (Abweichung TA-D4, Muster sales.pickup.lookup)
  risk: 'write',
  scope: 'record',
  publicApi: true,
  assistant: 'never',
  // Postleitzahl und Nummer nicht ins versiegelte Audit-Protokoll (TA-D8)
  auditInput: 'omit',
})

// ---- Bestellungen am Telefon (docs/integration/telefonassistent.md §16) -----------------------------------------------------------------
//
// Drei Verträge mit dem Recht `sales.phone.order` (nur mit channel-setup.ts --allow-orders): Geschäftskunden erkennen (nur ja oder nein),
// Warenkorb prüfen und rechnen (legt nichts an), Bestellung anlegen – immer im Zustand „Eingegangen“, nichts reserviert, Herkunft
// „Telefon (KI)“. Preise, Steuer und Rückgutbeträge rechnet der Belegkern, Gebühren, Gebiet, Mindestwert und Fenster Ordaneo selbst.

const ORDER = 'sales.phone.order'
const zZip = z.string().trim().regex(/^[0-9]{5}$/, { error: 'Eine Postleitzahl hat fünf Ziffern.' })
const zE164 = z.string().trim().regex(/^\+[1-9][0-9]{6,14}$/, { error: 'Die Rufnummer hat die Form +49… (E.164).' })

/** Geschäftskunde: Kundennummer und Postleitzahl einer seiner Anschriften – in jedem Aufruf neu geprüft */
const zCustomerProof = z.strictObject({
  number: zText(40).min(1, { error: 'Bitte die Kundennummer nennen.' }),
  zip: zZip,
})

/** Warenkorb, Lieferart, Ort und Termin – gleich bei Prüfen und Absenden */
const phoneOrderShape = {
  fulfillment: z.enum(['delivery', 'pickup'], { error: 'Die Lieferart ist delivery oder pickup.' }),
  /** Lieferung: liefernder Standort (Standard: Lager des Zugangs); Abholung: der Markt (Pflicht) */
  locationCode: zText(20).min(1).optional(),
  day: zDay,
  timeFrom: zTime.optional(),
  timeTo: zTime.optional(),
  lines: z
    .array(
      z.strictObject({
        sku: zText(64).min(1, { error: 'Die Artikelnummer fehlt.' }),
        /** Gebinde laut Sortiment (`salesUnit.code`); ohne Angabe das Verkaufsgebinde */
        unit: zText(20).min(1).optional(),
        /** ganze Gebinde */
        qty: z.int({ error: 'Die Menge ist eine ganze Zahl.' }).min(1, { error: 'Die Menge ist mindestens 1.' }).max(PHONE_ORDER_MAX_LINE_QTY, { error: `Höchstens ${PHONE_ORDER_MAX_LINE_QTY} je Position.` }),
      }),
    )
    .min(1, { error: 'Die Bestellung braucht mindestens eine Position.' })
    .max(PHONE_ORDER_MAX_LINES, { error: `Höchstens ${PHONE_ORDER_MAX_LINES} Positionen je Bestellung.` }),
  /** Lieferung: Postleitzahl der Lieferanschrift; Abholung: Postleitzahl der Anruferin bzw. des Anrufers (für die Auftragsauskunft) */
  zip: zZip,
  customer: zCustomerProof.optional(),
  carryService: z.boolean().optional(),
}

const zPhoneWindow = z.object({ day: z.string(), from: zHm, to: zHm })
const zPhoneHint = z.object({ code: z.enum(PHONE_ORDER_HINTS), message: z.string() })

export const phoneCheckCustomer = defineContract({
  name: 'sales.phone.checkCustomer',
  title: 'Geschäftskunden für den Telefonassistenten erkennen',
  description: 'Prüft Kundennummer und Postleitzahl einer Anschrift – Antwort nur ja oder nein, gesperrt nach Fehlversuchen.',
  llm: {
    description:
      'Schnittstelle des Telefonassistenten (öffentliche API, Recht sales.phone.order). Ja nur für aktive, nicht gesperrte Geschäfts- oder ' +
      'öffentliche Kunden, deren Anschrift diese Postleitzahl trägt (nie der Sammelkunde) – sonst immer `{ match: false }`. Keine Namen, keine ' +
      'Anschrift, keine Art. 5 Fehlversuche je Nummer oder 20 je Zugang in 60 Minuten → 429. Ein Treffer gilt drei Stunden für Prüfen und Absenden.',
    keywords: ['Kundennummer', 'Geschäftskunde', 'Telefon-Bestellung', 'Telefonassistent', 'Schnittstelle'],
  },
  input: zCustomerProof,
  output: z.strictObject({ match: z.boolean() }),
  permission: ORDER,
  // zählt Versuche (Sperre) – darum ändernd; „kein Treffer“ ist ein Ergebnis, kein Fehler (wie lookupOrder, TA-D4)
  risk: 'write',
  scope: 'record',
  publicApi: true,
  assistant: 'never',
  auditInput: 'omit',
})

export const phoneQuoteOrder = defineContract({
  name: 'sales.phone.quoteOrder',
  title: 'Telefon-Bestellung prüfen und rechnen',
  description: 'Prüft einen Warenkorb gegen Sortiment, Lieferregeln, Belegung und Grenzen und rechnet ihn wie der Beleg – ohne etwas anzulegen.',
  llm: {
    description:
      'Schnittstelle des Telefonassistenten (öffentliche API, Recht sales.phone.order). Rechnet den Warenkorb über den Belegkern (Preise, Steuer, ' +
      'Rückgut) und die Lieferregeln (Gebiet, Liefergebühr, Tragservice, Mindestwert, Fenster mit Belegung) – genau wie sales.phone.submitOrder, ' +
      'aber alles wird zurückgerollt. `ok` = absenden möglich, sonst `problems` mit deutschem Satz und bei vollen Fenstern `alternatives`. ' +
      'Geschäftskunden (`customer`, vorher sales.phone.checkCustomer): keine Beträge (`totals: null`, Preise null).',
    keywords: ['Warenkorb prüfen', 'Zusammenfassung', 'Liefergebühr', 'Zeitfenster', 'Telefon-Bestellung', 'Telefonassistent', 'Schnittstelle'],
  },
  input: z.strictObject(phoneOrderShape),
  output: z.object({
    ok: z.boolean(),
    customerKind: z.enum(['private', 'business']),
    problems: z.array(z.object({ code: z.enum(PHONE_ORDER_PROBLEMS), message: z.string(), cents: z.int().nullable() })),
    hints: z.array(zPhoneHint),
    locationCode: z.string(),
    lines: z.array(
      z.object({
        sku: z.string(),
        name: z.string(),
        unit: z.string(),
        unitLabel: z.string(),
        qty: z.int(),
        minAge: z.int(),
        /** je Gebinde in der Preisbasis des Auftrags (privat: brutto); null bei Geschäftskunden */
        unitPriceCents: z.int().nullable(),
        /** Rückgut je Gebinde; null bei Geschäftskunden */
        depositCents: z.int().nullable(),
        /** Zeilenbetrag ohne Rückgut; null bei Geschäftskunden */
        lineCents: z.int().nullable(),
        stock: z.enum(['ok', 'low', 'short']),
      }),
    ),
    /** brutto, Endbetrag = grossCents (Ware + Rückgut + Gebühren); null bei Geschäftskunden oder ohne rechenbaren Warenkorb */
    totals: z.object({ goodsCents: z.int(), depositCents: z.int(), deliveryFeeCents: z.int(), carryCents: z.int(), grossCents: z.int() }).nullable(),
    window: zPhoneWindow.extend({ free: z.int().nullable() }).nullable(),
    alternatives: z.array(zPhoneWindow).max(4),
    minOrder: z.object({ cents: z.int(), met: z.boolean(), missingCents: z.int().nullable() }).nullable(),
    paymentMethods: z.array(z.enum(['cash', 'ec', 'invoice'])),
    /** höchstes Mindestalter der Positionen (0 = keins) */
    ageCheck: z.int(),
    serverTime: z.date(),
  }),
  permission: ORDER,
  // rechnet über einen Entwurf, der in einem Sicherungspunkt zurückgerollt wird (isolated) – darum ändernd, aber ohne Wirkung
  risk: 'write',
  scope: 'record',
  publicApi: true,
  assistant: 'never',
  auditInput: 'omit',
})

export const phoneSubmitOrder = defineContract({
  name: 'sales.phone.submitOrder',
  title: 'Telefon-Bestellung anlegen (unbestätigt)',
  description: 'Legt nach dem Ja der Anruferin bzw. des Anrufers einen Auftrag im Zustand „Eingegangen“ an – nichts wird reserviert, der Vertrieb bestätigt.',
  llm: {
    description:
      'Schnittstelle des Telefonassistenten (öffentliche API, Recht sales.phone.order). Dieselben Felder wie sales.phone.quoteOrder plus Kennung ' +
      '(`voc-best-<32 Hex>`), Kontakt, Zahlart, Volljährigkeit bestätigt, erwarteter Endbetrag und Zeitpunkt des Ja. Im Gespräch (Ja ≤ 2 Minuten alt) ' +
      'werden Verstöße gegen Lieferregeln abgelehnt (409/422), bei später Übertragung als Prüfhinweis angelegt – aber nur, wenn derselbe Warenkorb ' +
      'kurz vor dem Ja ohne Einwände geprüft wurde. Liegt der neue Betrag über dem erwarteten → 409 `quote_changed`. Antwort mit Kanalnummer „T-…“ ' +
      '(zufällig) und `callbackRequired` (das Team ruft vor dem Bestätigen zurück). Gleiche Kennung und gleiche Angaben → dieselbe Bestellung ' +
      '(`replayed`), andere Angaben → 409 `order_id_reused`, vom Team verworfen → 409 `order_discarded`. Geschäftskunden nur Lieferung.',
    keywords: ['Bestellung anlegen', 'Telefon-Bestellung', 'Bestellnummer', 'Telefonassistent', 'Schnittstelle'],
  },
  input: z.strictObject({
    externalOrderId: z.string().regex(PHONE_ORDER_ID, { error: 'Die Kennung der Bestellung hat die Form voc-best- und 32 Hex-Zeichen.' }),
    ...phoneOrderShape,
    contact: z.strictObject({
      name: zText(120).min(1, { error: 'Der Name fehlt.' }),
      /** Rückrufnummer */
      phone: zE164,
      /** caller_id = die übertragene Nummer bestätigt; other = eine andere Nummer genannt */
      phoneSource: z.enum(['caller_id', 'other'], { error: 'Die Herkunft der Nummer ist caller_id oder other.' }),
      /** Pflicht bei privater Lieferung */
      street: zText(200).min(1).optional(),
      city: zText(120).min(1).optional(),
      /** Hinweis für den Fahrer */
      note: zText(300).optional(),
    }),
    /** übertragene Nummer des Anrufs – nur für Grenzen und den Hinweis „Nummer nicht bestätigt“, gespeichert nur als Prüfsumme */
    callerId: zE164.optional(),
    /** Pflicht bei Privatkunden; bei Geschäftskunden nicht erlaubt (es gilt die vereinbarte Bedingung) */
    paymentMethod: z.enum(PHONE_PAYMENT_METHODS, { error: 'Die Zahlart ist cash oder ec.' }).optional(),
    /** Pflicht, wenn eine Position ein Mindestalter hat: Die Person hat am Telefon bestätigt, volljährig zu sein (Annahme „Alkohol nur an
     *  Erwachsene“, §16.12) – kontrolliert wird bei der Übergabe */
    ageConfirmed: z.boolean().optional(),
    /** angemeldete Rückgabe (Gebinde) – Hinweis für den Fahrer */
    emptiesCrates: z.int().min(1).max(50).optional(),
    /** vorgelesener Endbetrag (Pflicht bei Privatkunden, bei Geschäftskunden nicht erlaubt) */
    expectedGrossCents: z.int().min(0).optional(),
    /** Zeitpunkt des Ja */
    placedAt: z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt nach ISO 8601 angeben.' }),
    /** Anruf in Vocaris (Rückfragen) */
    callRef: zText(64).optional(),
  }),
  output: z.object({
    orderId: zId,
    /** „T-100017“ – Bestellnummer für die Anruferin bzw. den Anrufer, mit der Postleitzahl auch für sales.phone.lookupOrder */
    channelNumber: z.string(),
    status: z.string(),
    statusLabel: z.string(),
    replayed: z.boolean(),
    fulfillment: z.enum(['delivery', 'pickup']),
    locationCode: z.string(),
    day: z.string().nullable(),
    window: z.object({ from: zHm, to: zHm }).nullable(),
    /** Endbetrag brutto; null bei Geschäftskunden */
    grossCents: z.int().nullable(),
    pickupCode: z.string().nullable(),
    hints: z.array(zPhoneHint),
    /** Vor dem Bestätigen ruft das Team zurück (andere Nummer, neue Anschrift, später übertragen, Geschäftskunde – §16.12) */
    callbackRequired: z.boolean(),
  }),
  permission: ORDER,
  risk: 'write',
  scope: 'record',
  publicApi: true,
  assistant: 'never',
  // Name, Rückrufnummer, Anschrift: nicht ins versiegelte Audit-Protokoll – sie stehen nur am Auftrag (TB-13)
  auditInput: 'omit',
})

/**
 * „Rückruf erfolgt“ an einer Telefon-Bestellung des Telefonassistenten vermerken (§16.12) – Voraussetzung fürs Bestätigen, wenn die Bestellung
 * einen Rückruf braucht (andere Rückrufnummer, neue Anschrift, später übertragen, Geschäftskunde). Für den Vertrieb, nicht für Vocaris.
 */
export const orderPhoneCallback = defineContract({
  name: 'sales.order.phoneCallback',
  title: 'Rückruf zur Telefon-Bestellung vermerken',
  description: 'Vermerkt an einer Telefon-Bestellung des Telefonassistenten, dass der Vertrieb zurückgerufen hat – erst dann lässt sie sich bestätigen.',
  llm: {
    description:
      'Vermerkt „Rückruf erfolgt“ an einer unbestätigten Telefon-Bestellung (Herkunft „Telefon (KI)“). Bestellungen mit anderer Rückrufnummer, ' +
      'neuer Lieferanschrift, später Übertragung oder von Geschäftskunden lassen sich erst danach bestätigen. Nur für Aufträge, die noch nicht ' +
      'bestätigt sind.',
    keywords: ['Rückruf', 'Telefon-Bestellung', 'Telefonassistent', 'bestätigen'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({ callbackAt: z.date(), callbackBy: z.string() }),
  permission: 'sales.order.write',
  risk: 'write',
  scope: 'record',
})
